import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { MdHistory, MdSchedule } from "react-icons/md";
import SearchableSelect from "../../../../components/searchableSelect/SearchableSelect";
import { getEmployeeCapacityHistory, setEmployeeCapacity } from "../../../../services/capacityApi";
import { EmployeeCapacityHistoryItem } from "../../../../interfaces/Capacity";

interface Option {
  id: number;
  name: string;
}
interface Props {
  employees: Option[];
}

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const fmtDate = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("pt-BR");
};

const EmployeeCapacityManager: React.FC<Props> = ({ employees }) => {
  const [employeeId, setEmployeeId] = useState<number | undefined>(undefined);
  const [history, setHistory] = useState<EmployeeCapacityHistoryItem[]>([]);
  const [hours, setHours] = useState<string>("40");
  const [effectiveFrom, setEffectiveFrom] = useState<string>(todayIso());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = (id: number) => {
    setLoading(true);
    getEmployeeCapacityHistory(id)
      .then(setHistory)
      .catch((e) => console.error("Erro ao carregar histórico de capacity", e))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (employeeId == null) {
      setHistory([]);
      return;
    }
    load(employeeId);
  }, [employeeId]);

  // Valor vigente hoje = entrada mais recente com vigência <= hoje.
  const current = useMemo(() => {
    const today = todayIso();
    return history
      .filter((h) => h.effectiveFrom <= today)
      .sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? 1 : -1))[0];
  }, [history]);

  const save = async () => {
    if (employeeId == null) return;
    const h = Number(hours);
    if (!(h > 0)) {
      toast.error("Informe um capacity em horas maior que zero.");
      return;
    }
    setSaving(true);
    try {
      await setEmployeeCapacity(employeeId, h, effectiveFrom || undefined);
      toast.success("Capacity padrão definido a partir de " + fmtDate(effectiveFrom) + ".");
      load(employeeId);
    } catch (e) {
      console.error("Erro ao definir capacity", e);
      toast.error("Erro ao salvar o capacity padrão.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h4 className="flex items-center gap-1.5 text-sm font-bold text-navy-700 dark:text-white">
          <MdSchedule className="h-4 w-4 text-dv-blue-600 dark:text-dv-cyan-300" aria-hidden="true" /> Capacity padrão do colaborador
        </h4>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Defina as horas/semana padrão. A mudança vale a partir da data escolhida e <strong>não altera o
          capacity do passado</strong> (semanas anteriores mantêm o valor que estava vigente).
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <div className="md:col-span-2">
          <label htmlFor="ecm-employee" className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-300">
            Colaborador <span className="text-red-500">*</span>
          </label>
          <SearchableSelect
            placeholder="Selecione um colaborador…"
            value={employeeId ?? null}
            onChange={(v) => setEmployeeId(v == null ? undefined : Number(v))}
            options={employees.map((e) => ({ label: e.name, value: e.id }))}
          />
        </div>
        <div>
          <label htmlFor="ecm-hours" className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-300">
            Horas/semana <span className="text-red-500">*</span>
          </label>
          <input
            id="ecm-hours"
            type="number"
            min={0}
            step={0.5}
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
          />
        </div>
        <div>
          <label htmlFor="ecm-from" className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-300">
            A partir de
          </label>
          <input
            id="ecm-from"
            type="date"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
          />
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          type="button"
          onClick={save}
          disabled={employeeId == null || saving}
          className="dv-sheen flex items-center justify-center gap-1.5 rounded-xl bg-dv-gradient px-5 py-2.5 text-sm font-bold text-white shadow-dv-glow transition-all active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? "Salvando..." : "Definir capacity padrão"}
        </button>
        {employeeId != null && current && (
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Vigente hoje: <strong>{current.weeklyCapacityHours}h/semana</strong> (desde {fmtDate(current.effectiveFrom)})
          </span>
        )}
      </div>

      {employeeId != null && (
        <div>
          <p className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
            <MdHistory className="h-4 w-4 text-gray-400" aria-hidden="true" /> Histórico de vigências
          </p>
          {loading ? (
            <p className="text-sm text-gray-700 dark:text-gray-300">Carregando...</p>
          ) : history.length === 0 ? (
            <p className="rounded-xl border border-dashed border-gray-200 px-3 py-3 text-sm text-gray-700 dark:border-white/10 dark:text-gray-300">
              Sem vigências definidas — usa o capacity padrão atual do cadastro do colaborador.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white nina-surface shadow-dv-md dark:border-white/10 dark:bg-navy-800">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-navy-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">A partir de</th>
                    <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">Horas/semana</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr
                      key={h.id}
                      className="border-b border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700"
                    >
                      <td className="px-4 py-3 font-medium tabular-nums text-navy-700 dark:text-white">{fmtDate(h.effectiveFrom)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-300">{h.weeklyCapacityHours}h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmployeeCapacityManager;
